import * as monaco from 'monaco-editor';
import {
  BrowserMessageReader,
  BrowserMessageWriter,
  InitializeParams,
  InitializeRequest,
  InitializedNotification,
  LogMessageNotification,
  MessageType,
  NotificationHandler,
  PublishDiagnosticsNotification,
  TelemetryEventNotification,
  createProtocolConnection,
} from 'vscode-languageserver-protocol/browser';

import { setCachedConnection } from './ApolloGraphqlActionsProvider';
import { updateDiagnostics } from './ApolloGraphqlDiagnosticsProvider';
import { registerCapabilities } from './capabilities/registerCapabilities';
import { loadWorker } from './load-worker';
import { assertUnreachable } from './utils/assertUnreachable';
import { diagnosticsMapping } from './utils/convertVscodeToMonaco';
import { graphqlTokensProvider } from './utils/graphqlTokensProvider';

let isRegistered = false;
export const languageId = 'apollo-graphql';
export async function registerApolloGraphQLLanguage({
  version,
  enableDebugLogging,
  enableAutoComposition,
  forceFederation,
  handleWorkerTimeout,
  onTelemetry,
  onException,
}: {
  version: `${number}.${number}.${number}` | 'local';
  enableDebugLogging: boolean;
  enableAutoComposition: boolean;
  forceFederation: boolean;
  handleWorkerTimeout: (reject: (err: Error) => void) => void;
  // This type comes right from the protocol. Telemetry is unstructured data.
  onTelemetry?: NotificationHandler<unknown>;
  onException: (message: string, extra?: Record<string, unknown>) => void;
}) {
  if (isRegistered) return;
  isRegistered = true;
  monaco.languages.register({ id: languageId, extensions: [languageId] });
  const monarchPromise = import(
    /* webpackChunkName: "graphql-monarch" */ 'monaco-editor/esm/vs/basic-languages/graphql/graphql'
  ).then(({ conf }) => {
    monaco.languages.setLanguageConfiguration(languageId, conf);
  });
  monaco.languages.setTokensProvider(languageId, graphqlTokensProvider);

  const worker = await loadWorker(
    version,
    enableDebugLogging,
    handleWorkerTimeout,
  );

  const connection = createProtocolConnection(
    new BrowserMessageReader(worker),
    new BrowserMessageWriter(worker),
  );
  setCachedConnection(connection);

  connection.onNotification(PublishDiagnosticsNotification.type, (params) => {
    const markers = params.diagnostics.map((di) =>
      diagnosticsMapping.vscodeToMonaco(di, onException),
    );
    updateDiagnostics(params.uri, markers);
    const model = monaco.editor
      .getModels()
      .find((m) => m.uri.toString(true) === params.uri);
    if (model) {
      monaco.editor.setModelMarkers(model, languageId, markers);
    } else {
      onException('Language server published diagnositics for unknown model', {
        uri: params.uri,
      });
    }
  });

  connection.onNotification(LogMessageNotification.type, (params) => {
    switch (params.type) {
      case MessageType.Error:
        // eslint-disable-next-line no-console
        console.error(params.message);
        break;
      case MessageType.Warning:
        // eslint-disable-next-line no-console
        console.warn(params.message);
        break;
      case MessageType.Info:
        // console.info(params.message);
        break;
      case MessageType.Log:
        // console.log(params.message);
        break;
      // case MessageType.Debug:
      //   // console.debug(params.message);
      //   break;
      default:
        assertUnreachable(params.type);
    }
  });

  if (onTelemetry) {
    connection.onNotification(TelemetryEventNotification.type, onTelemetry);
  }

  connection.listen();

  const INITIALIZE_PARAMS: InitializeParams = {
    processId: null,
    rootUri: null,
    capabilities: { textDocument: { completion: { contextSupport: true } } },
    initializationOptions: {
      enableAutoComposition,
      forceFederation,
    },
  };

  const { capabilities } = await connection.sendRequest(
    InitializeRequest.type,
    INITIALIZE_PARAMS,
  );

  const addModel = registerCapabilities({
    languageId,
    connection,
    capabilities,
    onException,
  });

  await connection.sendNotification(InitializedNotification.type, {});

  for (const model of monaco.editor.getModels()) {
    addModel(model);
  }

  return monarchPromise;
}
